import _ from 'underscore'

import api from 'src/store/api'
import dates from 'src/utils/dates'
import http from 'src/store/http'
import transform from 'src/utils/transform'

import actionNames from './actionNames'
import { fireanalytics } from '@/store/firebase'

const actions = {
  async getData({ commit, dispatch }, { params, query, selectedSearch }) {
    commit('tools/GET_DATA_REFRESHED_TIME_REQUESTED', true, { root: true })

    if (selectedSearch) {
      query.networkCode = selectedSearch.networkCode
      query.publisherId = selectedSearch.publisherId
      query.teamId = selectedSearch.teamId
    }

    const updateView = () => {
      const { baseReportName, routeName } = params
      const endpointKey = baseReportName || routeName
      return dispatch('tools/updateView', { endpointKey }, { root: true })
    }
    const setView = () => {
      const { reportPath } = query
      const haveSelectedSearch = !!(reportPath && selectedSearch)
      return haveSelectedSearch && dispatch('tools/setView', { params, query }, { root: true })
    }

    return setView() || updateView()
  },

  openTab(context, url) {
    window.open(url, '_blank')
  },
  openTabToLineItem({ dispatch }, item) {
    const url = item.lineItemGamLink
    dispatch('openTab', url)
  },
  openTabToOrder({ dispatch }, item) {
    const url = item.orderGamLink
    dispatch('openTab', url)
  },

  /* Discrepancy */

  async getDiscrepancyCreatives({ commit }, params) {
    commit(actionNames.GET_DISCREPANCY_CREATIVES_REQUESTED)

    const catcher = (err) => {
      const error = http.catch(err)
      commit(actionNames.GET_DISCREPANCY_CREATIVES_FAILED, error)
      return error
    }

    const creatives = await api.getDiscrepancyCreatives(params).catch(catcher)
    const { content, error } = creatives

    if (error) return catcher(creatives)

    const items = _.sortBy(content, 'date').reverse()

    commit(actionNames.GET_DISCREPANCY_CREATIVES_SUCCEEDED, items)

    return creatives
  },

  get10DayHistoryRequests({ commit }, params) {
    const catcher = (err) => {
      const error = http.catch(err)
      commit(actionNames.GET_10_DAY_HISTORY_FAILED, error)
      return error
    }

    const endDate = dates.now()
    const startDate = endDate.clone().subtract(10, 'days')
    const data = { ...params, startDate, endDate }

    const deliveryRequest = api
      .getDeliveryReportDataForLineItemWithDates(data)
      .catch(catcher)

    const pacingRequest = api
      .getPacingReportDataForLineItemWithDates(data)
      .catch(catcher)

    const requests = [deliveryRequest, pacingRequest]

    return Promise.all(requests)
  },
  async get10DayHistory({ commit, dispatch }, params) {
    commit(actionNames.GET_10_DAY_HISTORY_REQUESTED)

    fireanalytics.event('goal_initialization', {
      category: '10DayHistory',
      description: '10 Day History',
      label: `10 Day History Requested for Line Item ${params.lineItemId} in Publisher ${(params.teamId && `${params.publisherId}/${params.teamId}`) || params.publisherId}`,
    })

    const promises = await dispatch('get10DayHistoryRequests', params)
    const data = await promises
    const [deliveryResponse, pacingResponse] = data
    const { content: delivery, error: deliveryError } = deliveryResponse
    const { content: pacing, error: pacingError } = pacingResponse

    // @TODO Combine errors when responding to multiple requests.
    if (deliveryError) return deliveryResponse
    if (pacingError) return pacingResponse

    const raw = [delivery, pacing]
    const results = transform.processLineItemHistoryData(raw)

    commit(actionNames.GET_10_DAY_HISTORY_SUCCEEDED, results)

    fireanalytics.event('goal_completion', {
      category: 'History',
      description: '10 Day History',
      label: `10 Day History Succeeded for Line Item ${params.lineItemId} in Publisher ${(params.teamId && `${params.publisherId}/${params.teamId}`) || params.publisherId}`,
    })

    return results
  },

  getCreativeDeliveryByLineItemId({ commit }, params) {
    const catcher = (err) => {
      const error = http.catch(err)
      commit(actionNames.GET_CREATIVES_REPORT_FAILED, error)
      return error
    }

    return api.getCreativesReport(params).catch(catcher)
  },
  async getCreativesReport({ commit, dispatch }, params) {
    commit(actionNames.GET_CREATIVES_REPORT_REQUESTED)

    const catcher = (err) => {
      const error = http.catch(err)
      commit(actionNames.GET_CREATIVES_REPORT_FAILED, error)
      return error
    }

    const reportResponse = await dispatch('getCreativeDeliveryByLineItemId', params)
    const { content: reportCreatives, error: reportError } = reportResponse

    if (reportError) return catcher(reportResponse)

    const groups = reportCreatives.reduce((creativeGroups, creative) => {
      const { creativeCode, creativeName, creativeSize } = creative

      if (!creativeGroups[creativeCode]) {
        creativeGroups[creativeCode] = {
          creativeCode,
          creativeName,
          creativeSize,
          rows: [],
        }
      }

      creativeGroups[creativeCode].rows.push(creative)

      return creativeGroups
    }, {})

    const valueGetter = (row) => dates.get(row.date).unix()
    const items = Object.keys(groups).reduce((creativeGroups, creativeCode) => {
      const thisCreative = groups[creativeCode]
      const { rows } = thisCreative

      creativeGroups[creativeCode] = thisCreative
      creativeGroups[creativeCode].rows = _.sortDscByCaseInsensitive(rows, valueGetter)

      return creativeGroups
    }, {})

    commit(actionNames.GET_CREATIVES_REPORT_SUCCEEDED, items)

    return items
  },
  async getCreatives({ commit }, query) {
    commit(actionNames.GET_CREATIVE_REQUESTED)

    const catcher = (err) => {
      const error = http.catch(err)
      commit(actionNames.GET_CREATIVE_FAILED, error)
      return error
    }

    const { code: lineItemId, networkCode } = query

    const apiParams = { licaIds: [{ lineItemId }], networkCode }
    const licaResponse = await api.getGamLicas(apiParams)
    const { content: licaContent, error: licaError } = licaResponse

    if (licaError) return catcher(licaResponse)

    const ids = licaContent.map((lica) => {
      const { creativeId: id } = lica
      return { id }
    })
    const creativeResponse = await api.postGamCreatives({ ids, networkCode })
    const { content: creatives, error: creativeError } = creativeResponse

    if (creativeError) return catcher(creativeResponse)

    commit(actionNames.GET_CREATIVE_SUCCEEDED, creatives)

    return licaResponse
  },
  async getTearsheet({ commit }, query) {
    commit(actionNames.GET_TEARSHEET_REQUESTED)

    const catcher = (err) => {
      const error = http.catch(err)
      commit(actionNames.GET_TEARSHEET_FAILED, error)
      return error
    }

    const licaResponse = await api.postTearsheet(query).catch(catcher)
    const { content: tearsheetUrl, error } = licaResponse

    if (error) return catcher(licaResponse)

    const { creativeId } = query

    commit(actionNames.GET_TEARSHEET_SUCCEEDED, { creativeId, tearsheetUrl })

    return licaResponse
  },

  showLineItemHistoryModal({ commit, dispatch }, item) {
    commit('currentRecord', item)
    commit('isLineItemHistoryModalVisible', true)

    dispatch('get10DayHistory', item)
  },
  showCreativesReportModal({ commit, dispatch }, item) {
    commit('currentRecord', item)
    commit('isCreativesReportModalVisible', true)

    dispatch('getCreativesReport', item)
  },
  showTearsheetModal({ commit, dispatch }, item) {
    commit('currentRecord', item)
    commit('isCreativeModalVisible', true)

    dispatch('getCreatives', item)
  },
  showCreativesDiscrepancyModal({ commit, dispatch }, item) {
    commit('currentRecord', item)
    commit('isDiscrepancyCreativeModalVisible', true)
    dispatch('getDiscrepancyCreatives', item)
  },
}

export default actions
